import { FC, Fragment, useState } from 'react';
import CloserLookStudentRow from './CloserLookStudentRow';
import Paginator, { PAGE_SIZE } from './Paginator';
import { isCriticalStudent, StudentReport } from './closerLookHelper';

/**
 * Where the section is being rendered:
 * - `dialog`: it fills a dialog of its own (teacher dashboard), which already shows the title in
 *   its header and has room for the guidance copy.
 * - `embedded`: it sits under another list inside a wider dialog (diagnostics report), so it
 *   brings its own heading and drops the guidance copy to stay compact.
 */
type Variant = 'dialog' | 'embedded';

interface Props {
  /** Students already narrowed to "taking too long" — see selectTakingTooLongStudents. */
  students: StudentReport[];
  variant?: Variant;
  onConfirmDialogToggle?: (open: boolean) => void;
}

interface GroupProps {
  title: string;
  titleClassName: string;
  borderColorClass: string;
  students: StudentReport[];
  reserveActionsColumn: boolean;
  onConfirmDialogToggle?: (open: boolean) => void;
}

const GroupColumnHeaders: FC<{ reserveActionsColumn: boolean }> = ({ reserveActionsColumn }) => (
  <Fragment>
    <span className="tw-text-xs tw-font-atkinson tw-text-neutral-500 tw-uppercase tw-w-20 tw-text-center tw-font-semibold">
      Q&apos;s Answered
    </span>
    <span className="tw-text-xs tw-font-atkinson tw-text-neutral-500 tw-uppercase tw-w-16 tw-text-center tw-font-semibold">
      Time Spent
    </span>
    {reserveActionsColumn && <span className="tw-w-44 tw-shrink-0" />}
  </Fragment>
);

/** One severity group ("Critical" / "Mild") with its own column headers and pagination. */
const StudentGroup: FC<GroupProps> = ({
  title,
  titleClassName,
  borderColorClass,
  students,
  reserveActionsColumn,
  onConfirmDialogToggle,
}) => {
  const [page, setPage] = useState(0);

  if (students.length === 0) return null;

  const totalPages = Math.ceil(students.length / PAGE_SIZE);
  const pageStudents = students.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <>
      <div className="nc_diagnostic-modal-table">
        <div
          className={`tw-flex tw-items-center tw-gap-2 tw-py-1 tw-px-3 tw-border-x-0 tw-border-t-0 ${borderColorClass} tw-border-b tw-border-solid`}
        >
          <span className={titleClassName}>{title}</span>
          <GroupColumnHeaders reserveActionsColumn={reserveActionsColumn} />
        </div>
        {pageStudents.map(student => (
          <CloserLookStudentRow
            key={student.student.id}
            student={student}
            variant="too-long"
            borderColorClass={`tw-border-x-0 tw-border-t-0 ${borderColorClass}`}
            reserveActionsColumn={reserveActionsColumn}
            onConfirmDialogToggle={onConfirmDialogToggle}
          />
        ))}
      </div>
      <Paginator page={page} total={students.length} totalPages={totalPages} onChange={setPage} label={title} />
    </>
  );
};

/**
 * The "taking too long" students, split into critical (>90min) and mild (70-90min).
 *
 * Shared by the teacher dashboard's "closer look" modal and the diagnostics report's
 * "Diagnostic Progress" modal so both keep the same thresholds, copy and layout.
 */
const TakingTooLongSection: FC<Props> = ({ students, variant = 'dialog', onConfirmDialogToggle }) => {
  if (students.length === 0) return null;

  const criticalStudents = students.filter(isCriticalStudent);
  const mildStudents = students.filter(student => !isCriticalStudent(student));
  // The dialog variant keeps an empty slot where the "finished quickly" tab shows its row
  // actions, so both tabs line up. Embedded there is no such tab, so the columns use the full width.
  const reservesActionsColumn = variant === 'dialog';

  return (
    <>
      {variant === 'embedded' && (
        <h3 className="tw-text-base tw-font-bold tw-font-atkinson tw-text-neutral-950 tw-mt-6 tw-mb-3">
          Students who need a closer look
        </h3>
      )}

      {variant === 'dialog' && (
        <div className="tw-rounded-xl tw-border-2 tw-border-solid tw-border-neutral-100 tw-p-3 tw-flex tw-flex-col tw-gap-y-5">
          <div className="tw-flex tw-flex-row tw-gap-x-2">
            <span className="tw-text-sm tw-font-atkinson tw-text-neutral-950">
              <span className="tw-font-bold">Check in with these students first</span>
              <br></br>
              See what’s slowing them down, and remind them it’s ok to skip questions they don’t know. Taking longer can
              also be expected, especially for students with accommodations.
            </span>
          </div>
        </div>
      )}

      <StudentGroup
        title="Critical: >90min"
        titleClassName="tw-text-base tw-font-bold tw-font-atkinson tw-text-red-700 tw-flex-1 tw-flex tw-items-center tw-gap-1"
        borderColorClass="!tw-border-b-red-50"
        students={criticalStudents}
        reserveActionsColumn={reservesActionsColumn}
        onConfirmDialogToggle={onConfirmDialogToggle}
      />
      <StudentGroup
        title="Mild: 70–90min"
        titleClassName="tw-text-base tw-font-bold tw-font-atkinson tw-text-red-600 tw-flex-1"
        borderColorClass="!tw-border-b-neutral-200"
        students={mildStudents}
        reserveActionsColumn={reservesActionsColumn}
        onConfirmDialogToggle={onConfirmDialogToggle}
      />
    </>
  );
};

export default TakingTooLongSection;
